import React, { useLayoutEffect } from 'react';

import { Announce, Nav, Footer, DemoModal } from './sections';

const PAGE_BODY_CLASSES = [
  'cheqr-pricing-page',
  'cheqr-contact-page',
  'cheqr-team-page',
  'cheqr-case-study-page',
  'cheqr-legal-page',
];

const normalizePath = (pathname: string) => pathname.replace(/\/$/, '') || '/';

const isCaseStudyDetailPath = (pathname: string) =>
  /^\/case-studies\/[^/]+\/?$/.test(pathname);

const getPageBodyClass = (pathname: string): string | null => {
  const path = normalizePath(pathname);
  if (path === '/pricing') return 'cheqr-pricing-page';
  if (path === '/contact') return 'cheqr-contact-page';
  if (path === '/team') return 'cheqr-team-page';
  if (path === '/privacy' || path === '/terms') return 'cheqr-legal-page';
  if (isCaseStudyDetailPath(pathname)) return 'cheqr-case-study-page';
  return null;
};

const applyMarketingTokens = (pathname: string) => {
  const root = document.documentElement;
  const path = normalizePath(pathname);

  root.style.setProperty('--cyan', '#1FD5F4');
  root.style.setProperty('--navy', '#0B1A3F');
  root.style.setProperty('--cream', '#F4F6FB');
  root.style.setProperty('--font-display', '"Sora", ui-sans-serif, system-ui, sans-serif');
  root.style.setProperty(
    '--font-body',
    path === '/'
      ? '"Albert Sans", ui-sans-serif, system-ui, sans-serif'
      : '"Manrope", ui-sans-serif, system-ui, sans-serif'
  );

  if (path === '/' || path === '/pricing' || path === '/team') {
    root.style.setProperty('--font-mono', '"JetBrains Mono", ui-monospace, monospace');
  } else {
    root.style.removeProperty('--font-mono');
  }
};

export const isAppLayoutRoute = (pathname: string) => {
  const path = normalizePath(pathname);
  if (path === '/') return true;
  if (/^\/case-studies(\/|$)/.test(pathname)) return true;
  return ['/pricing', '/contact', '/team', '/privacy', '/terms'].includes(path);
};

type AppLayoutProps = {
  pathname: string;
  children: React.ReactNode;
};

const AppLayout: React.FC<AppLayoutProps> = ({ pathname, children }) => {
  useLayoutEffect(() => {
    applyMarketingTokens(pathname);
    document.body.classList.add('cheqr-home-page');
    PAGE_BODY_CLASSES.forEach((cls) => document.body.classList.remove(cls));
    const pageClass = getPageBodyClass(pathname);
    if (pageClass) document.body.classList.add(pageClass);

    return () => {
      document.body.classList.remove('cheqr-home-page', ...PAGE_BODY_CLASSES);
    };
  }, [pathname]);

  useLayoutEffect(() => () => {
    const root = document.documentElement;
    root.style.removeProperty('--cyan');
    root.style.removeProperty('--navy');
    root.style.removeProperty('--cream');
    root.style.removeProperty('--font-display');
    root.style.removeProperty('--font-body');
    root.style.removeProperty('--font-mono');
  }, []);

  return (
    <>
      {normalizePath(pathname) === '/' ? <Announce /> : null}
      <Nav />
      {children}
      <Footer />
      <DemoModal />
    </>
  );
};

export default AppLayout;
